import { Graphics } from 'pixi.js';
import * as THREE from 'three';
import { TILE_SIZE } from '../labyrinth/Labyrinth';
import type { Robot } from '../labyrinth/Robot';
import type { SpriteBaker, BakedSprite } from './SpriteBaker';

const TEX_PX     = 128;
const HALF_EXTENT = TILE_SIZE * 0.6;
const Y_OFFSET   = 0.3;

/**
 * Animated destination marker drawn on the floor at robot.marker. Mirrors the
 * pulsing ring + dot + crosshair from LabyrinthState.drawMarker.
 */
export class Marker3D {
  readonly group = new THREE.Group();
  private readonly geo: THREE.PlaneGeometry;
  private readonly material: THREE.MeshBasicMaterial;
  private readonly mesh: THREE.Mesh;
  private readonly sprite: BakedSprite;

  constructor(private readonly baker: SpriteBaker) {
    this.sprite = this.baker.createSprite(TEX_PX);
    this.material = new THREE.MeshBasicMaterial({
      map: this.sprite.texture,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    this.geo = new THREE.PlaneGeometry(HALF_EXTENT * 2, HALF_EXTENT * 2).rotateX(-Math.PI / 2);
    this.mesh = new THREE.Mesh(this.geo, this.material);
    this.mesh.renderOrder = 3;
    this.mesh.visible = false;
    this.group.add(this.mesh);
  }

  sync(robot: Robot, frameTime: number): void {
    const m = robot.marker;
    if (!m) {
      this.mesh.visible = false;
      return;
    }
    const [mx, my] = m;
    this.mesh.visible = true;
    this.mesh.position.set(mx * TILE_SIZE, Y_OFFSET, my * TILE_SIZE);

    this.baker.bake(this.sprite, HALF_EXTENT, (scratch) => {
      const g = new Graphics();
      scratch.addChild(g);
      drawMarkerLocal(g, frameTime);
    });
  }

  dispose(): void {
    this.group.remove(this.mesh);
    this.material.dispose();
    this.geo.dispose();
    this.baker.disposeSprite(this.sprite);
  }
}

function drawMarkerLocal(g: Graphics, t: number): void {
  const r = TILE_SIZE * 0.4;
  const pulse = 0.8 + Math.sin(t * 3) * 0.2;
  g.circle(0, 0, r * pulse);
  g.stroke({ color: 0x44aaff, width: 2, alpha: 0.8 });

  g.circle(0, 0, r * 0.2);
  g.fill({ color: 0x44aaff, alpha: 0.9 });

  const cr = r * 0.6;
  g.moveTo(-cr, 0); g.lineTo(-cr * 0.3, 0);
  g.moveTo( cr * 0.3, 0); g.lineTo( cr, 0);
  g.moveTo(0, -cr); g.lineTo(0, -cr * 0.3);
  g.moveTo(0,  cr * 0.3); g.lineTo(0,  cr);
  g.stroke({ color: 0x44aaff, width: 1.5, alpha: 0.6 });
}
